import React, {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {toAbsoluteUrl} from '../../../_metronic/helpers'
import {discoverTenantSettingsAccount} from '../auth/redux/AuthCRUD'
import {ProjectGuestPortalClient} from '../../infrastracture/api/ProjectGuestPortalClient'

const initialValues = {
  email: '',
  shortCode: '',
}

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  // Required for the same reason as on ProjectGuestLogin: the account is resolved from this, and
  // it's what picks the right user when the email address exists in more than one account.
  shortCode: Yup.string().trim().required('Company short code is required'),
})

// Mirrors app/modules/auth/components/ForgotPassword.tsx, but kept as its own page (rather
// than reused) so the "Cancel"/success state can point back to /projects/guest/login instead
// of the CRM's /login — same reasoning as ProjectGuestActivate/ProjectGuestLogin having their own shell.
export function ProjectGuestForgotPassword() {
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const [errorMessage, setErrorMessage] = useState('')

  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: async (values, {setSubmitting}) => {
      setHasErrors(undefined)

      const fail = (message: string) => {
        setHasErrors(true)
        setErrorMessage(message)
      }

      try {
        let discovery: {ok: boolean; data: any}

        try {
          discovery = await discoverTenantSettingsAccount(values.shortCode.trim())
        } catch (error) {
          // A short code matching no account answers 500 rather than a clean failure, so a
          // typo lands here. Reported as a not-found below rather than as a crash.
          console.error(error)
          discovery = {ok: false, data: null}
        }

        if (!discovery.ok) {
          fail('We could not find an account with that short code')
          return
        }

        const response = await ProjectGuestPortalClient.BeginPasswordReset(
          values.email,
          discovery.data.id
        )

        if (response.successful) {
          setHasErrors(false)
        } else {
          fail(
            response.validationErrors?.[0]?.message ||
              response.errorMessage ||
              response.message ||
              'Something went wrong. Please try again.'
          )
        }
      } catch (error) {
        console.error(error)
        fail('Something went wrong. Please try again.')
      } finally {
        setSubmitting(false)
      }
    },
  })

  return (
    <div className='d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed'>
      <div className='d-flex flex-center flex-column flex-column-fluid p-10 pb-lg-20'>
        <div className='w-lg-500px bg-white rounded shadow-sm p-10 p-lg-15 mx-auto'>
          <form
            className='form w-100'
            noValidate
            id='kt_project_guest_password_reset_form'
            onSubmit={formik.handleSubmit}
          >
            <div className='text-center mb-10'>
              <img
                alt='Logo'
                className='h-150px logo mb-5'
                src={toAbsoluteUrl('/media/logos/daysee_logo.png')}
              />
              <h1 className='text-dark mb-3'>Forgot Password?</h1>
              <div className='text-gray-400 fw-bold fs-4'>
                Enter your email and company short code to reset your project portal password.
              </div>
            </div>

            {hasErrors === true && (
              <div className='mb-lg-15 alert alert-danger'>
                <div className='alert-text font-weight-bold'>{errorMessage}</div>
              </div>
            )}

            {hasErrors === false && (
              <div className='mb-10 bg-light-info p-8 rounded'>
                <div className='text-info text-center'>
                  If your email exists, a reset password email will be sent
                </div>
              </div>
            )}

            {/* Kept on screen after an error so a mistyped email or short code can be retried. */}
            {hasErrors !== false && (
              <>
                <div className='fv-row mb-10'>
                  <label className='form-label fw-bolder text-gray-900 fs-6'>Email</label>
                  <input
                    type='email'
                    placeholder=''
                    autoComplete='off'
                    {...formik.getFieldProps('email')}
                    className={clsx(
                      'form-control form-control-lg form-control-solid',
                      {'is-invalid': formik.touched.email && formik.errors.email},
                      {
                        'is-valid': formik.touched.email && !formik.errors.email,
                      }
                    )}
                  />
                  {formik.touched.email && formik.errors.email && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.email}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className='fv-row mb-10'>
                  <label className='form-label fw-bolder text-gray-900 fs-6 required'>
                    Company Short Code
                  </label>
                  <input
                    type='text'
                    placeholder='Please enter your company short code'
                    autoComplete='off'
                    {...formik.getFieldProps('shortCode')}
                    className={clsx(
                      'form-control form-control-lg form-control-solid',
                      {'is-invalid': formik.touched.shortCode && formik.errors.shortCode},
                      {
                        'is-valid': formik.touched.shortCode && !formik.errors.shortCode,
                      }
                    )}
                  />
                  {formik.touched.shortCode && formik.errors.shortCode && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.shortCode}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
                  <button
                    type='submit'
                    id='kt_project_guest_password_reset_submit'
                    className='btn btn-lg btn-primary fw-bolder me-4'
                  >
                    {!formik.isSubmitting && <span className='indicator-label'>Submit</span>}
                    {formik.isSubmitting && (
                      <span className='indicator-progress' style={{display: 'block'}}>
                        Please wait...
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                  <Link to='/projects/guest/login'>
                    <button
                      type='button'
                      id='kt_project_guest_password_reset_cancel'
                      className='btn btn-lg btn-light-primary fw-bolder'
                      disabled={formik.isSubmitting || !formik.isValid}
                    >
                      Cancel
                    </button>
                  </Link>
                </div>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  )
}
